import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    useGetHelpNodeQuery,
    useCreateHelpNodeMutation,
    useUpdateHelpNodeMutation,
    useDeleteHelpNodeMutation,
    HelpNodeDetail,
    HelpNodePayload,
} from "../api";

const HelpNodeEditPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEdit = !!id;
    const nodeId = isEdit ? Number(id) : undefined;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const parentIdFromQuery = searchParams.get("parent_id");
    const scopeFromQuery = (searchParams.get("scope") as "public" | "private") || "private";
    const localeFromQuery = searchParams.get("locale") || "ru";

    const { data: existingNode, isLoading } = useGetHelpNodeQuery(nodeId!, {
        skip: !isEdit || !nodeId,
    } as any);

    const [form, setForm] = useState<HelpNodePayload>({
        parent_id: parentIdFromQuery ? Number(parentIdFromQuery) : null,
        scope: scopeFromQuery,
        locale: localeFromQuery,
        type: "article",
        slug: "",
        title: "",
        body_md: "",
        body_html: "",
        sort_order: 0,
        is_published: true,
    });

    const [createNode, { isLoading: isCreating }] = useCreateHelpNodeMutation();
    const [updateNode, { isLoading: isUpdating }] = useUpdateHelpNodeMutation();
    const [deleteNode, { isLoading: isDeleting }] = useDeleteHelpNodeMutation();

    useEffect(() => {
        if (existingNode && isEdit) {
            const n = existingNode as HelpNodeDetail;
            setForm({
                parent_id: n.parent_id,
                scope: n.scope,
                locale: n.locale,
                type: n.type,
                slug: n.slug,
                title: n.title,
                body_md: n.body_md ?? "",
                body_html: n.body_html ?? "",
                sort_order: n.sort_order,
                is_published: n.is_published,
            });
        }
    }, [existingNode, isEdit]);

    const isSaving = useMemo(
        () => isCreating || isUpdating,
        [isCreating, isUpdating],
    );

    const handleChange = (field: keyof HelpNodePayload, value: any) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (isEdit && nodeId) {
                await updateNode({ id: nodeId, data: form }).unwrap();
            } else {
                await createNode(form).unwrap();
            }
            navigate("/dashboard/help");
        } catch (err) {
            // Ошибка покажется через toast-интерсептор
            console.error("Failed to save help node", err);
        }
    };

    const handleDelete = async () => {
        if (!nodeId) return;
        if (!window.confirm("Удалить этот узел и всех его потомков?")) return;
        try {
            await deleteNode(nodeId).unwrap();
            navigate("/dashboard/help");
        } catch (err) {
            console.error("Failed to delete help node", err);
        }
    };

    if (isEdit && isLoading && !existingNode) {
        return <div className="text-sm text-gray-500">Загрузка...</div>;
    }

    return (
        <div className=" space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">
                    {isEdit ? "Редактирование узла справки" : "Новый узел справки"}
                </h1>
            </div>

            <form
                onSubmit={handleSubmit}
                className="bg-white dark:bg-gray-900 rounded shadow border border-gray-200 dark:border-gray-700 p-4 space-y-4"
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Scope
                        </label>
                        <select
                            value={form.scope}
                            onChange={(e) =>
                                handleChange(
                                    "scope",
                                    e.target.value as "public" | "private",
                                )
                            }
                            className="w-full border rounded px-2 py-1 text-sm"
                        >
                            <option value="public">public</option>
                            <option value="private">private</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Locale
                        </label>
                        <input
                            value={form.locale}
                            onChange={(e) => handleChange("locale", e.target.value)}
                            className="w-full border rounded px-2 py-1 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Тип
                        </label>
                        <select
                            value={form.type}
                            onChange={(e) =>
                                handleChange(
                                    "type",
                                    e.target.value as "section" | "article",
                                )
                            }
                            className="w-full border rounded px-2 py-1 text-sm"
                        >
                            <option value="section">Раздел</option>
                            <option value="article">Статья</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Slug
                        </label>
                        <input
                            value={form.slug}
                            onChange={(e) => handleChange("slug", e.target.value)}
                            className="w-full border rounded px-2 py-1 text-sm"
                            required
                        />
                        <p className="mt-1 text-xs text-gray-400">
                            Используется в URL на уровне этого узла.
                        </p>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">
                        Заголовок
                    </label>
                    <input
                        value={form.title}
                        onChange={(e) => handleChange("title", e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                        required
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Markdown (body_md)
                        </label>
                        <textarea
                            value={form.body_md ?? ""}
                            onChange={(e) => handleChange("body_md", e.target.value)}
                            className="w-full border rounded px-2 py-1 text-sm min-h-[160px]"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                            Предпочтительнее использовать Markdown, а HTML оставлять пустым.
                            При сохранении он автоматически будет конвертирован в HTML.
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Готовый HTML (body_html)
                        </label>
                        <textarea
                            value={form.body_html ?? ""}
                            onChange={(e) => handleChange("body_html", e.target.value)}
                            className="w-full border rounded px-2 py-1 text-sm min-h-[160px]"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                            Если заполнено и Markdown пустой, фронтенд может использовать HTML
                            напрямую.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Sort order
                        </label>
                        <input
                            type="number"
                            value={form.sort_order ?? 0}
                            onChange={(e) =>
                                handleChange("sort_order", Number(e.target.value) || 0)
                            }
                            className="w-full border rounded px-2 py-1 text-sm"
                        />
                    </div>
                    <div className="flex items-center gap-2 mt-6">
                        <input
                            id="is_published"
                            type="checkbox"
                            checked={form.is_published ?? true}
                            onChange={(e) =>
                                handleChange("is_published", e.target.checked)
                            }
                            className="mr-1"
                        />
                        <label htmlFor="is_published" className="text-sm">
                            Опубликовано
                        </label>
                    </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                    <div className="flex gap-2">
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="px-4 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                        >
                            {isSaving ? "Сохранение..." : "Сохранить"}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate("/dashboard/help")}
                            className="px-3 py-1.5 rounded border text-sm"
                        >
                            Отмена
                        </button>
                    </div>
                    {isEdit && nodeId && (
                        <button
                            type="button"
                            disabled={isDeleting}
                            onClick={handleDelete}
                            className="px-3 py-1.5 rounded bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-60"
                        >
                            {isDeleting ? "Удаление..." : "Удалить"}
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default HelpNodeEditPage;

